import * as lodash from "lodash";
import { Component, EventEmitter, OnInit, ViewChild } from "@angular/core";
import { FormControl, NgForm } from "@angular/forms";
import { SelectItem } from "primeng/primeng";
import { Observable } from "rxjs";

import {
    ITCIVpnConnection,
    ITCIVpnConnectionBrief,
    ITCIApplicationAuth,
    ITCIUserInfo,
    strEnum,
    TCITenantSelectorType,
    USER_ROLES
} from "@tci/common";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgSecurityService } from "../../ng-shared/ng-services/ng-security.service";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { ITCICEnvToolsComponentOutput, ITCIEnvToolsComponentInput } from "../../ng-tenant/tci-tenant-components";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";

@Component({
    selector: TCITenantSelectorType.TCI_ENV_TOOLS_VPN_CONNECTIONS.toString(),
    templateUrl: "./base-vpn.component.html",
    // Note: multiple css file in styleUrls MUST keep it in one line, otherwise sourcemap will be in disorder when debug in browser.
    styleUrls: [ "../ng-env-tools.component.css", "./base-vpn.component.css" ]
})
export class BaseVpnComponent
    extends AbstractTCITenantComponent <EventEmitter<string>, ITCIEnvToolsComponentInput, ITCICEnvToolsComponentOutput<any>>
    implements OnInit {

    private readonly CISCO_ANYCONNECT = "Cisco AnyConnect";
    private readonly JUNIPER_NETWORK_CONNECT = "Juniper Network Connect";

    private readonly VPN_CONNECTION_PROTOCOLS = strEnum([
        this.CISCO_ANYCONNECT,
        this.JUNIPER_NETWORK_CONNECT
    ]);

    private readonly EMPTY_VPN_CONNECTION: ITCIVpnConnection = {
        subscriptionId: "",
        userId: "",
        vpnAuthenticationGroup: "",
        vpnConnectionName: "",
        vpnConnectionId: "",
        vpnProtocol: this.VPN_CONNECTION_PROTOCOLS[this.CISCO_ANYCONNECT],
        vpnPassword: "",
        vpnServerName: "",
        vpnUserGroup: "",
        vpnUserName: ""
    };

    @ViewChild("vpnConnectionForm") private vpnConnectionForm: NgForm;
    private protocols: SelectItem[] = lodash.map(this.VPN_CONNECTION_PROTOCOLS, (value: string) => {
        return {
            label: value,
            value: value
        };
    });
    private user: ITCIUserInfo;
    private vpnConnectionBriefs: ITCIVpnConnectionBrief[] = [];
    private vpnConnections: ITCIVpnConnection[] = [];
    private vpnConnectionsToDisplay: ITCIVpnConnection[] = [];
    private vpnConnectionToSubmit: ITCIVpnConnection = null;
    private selectedVpnConnectionId = "";
    private selectedVpnConnection: ITCIVpnConnection = null;
    private keyword = "";
    private keywordFormControl: FormControl = new FormControl();
    private showDialog = false;
    private showDeleteConfirmation = false;
    private isReadonly = true;
    private isDeleting = false;
    private isLoading = false;
    private isNew = true;
    private isSubmitted = false;
    private isValidated = false;

    /**
     *
     */
    constructor(
        private toastr: NgToastrService,
        private userService: NgUserService,
        private vpnSecurity: NgSecurityService
    ) {
        super();
    }

    ngOnInit(): void {
        this.isLoading = true;
        this
            .userService
            .getUserInfo()
            .subscribe(
                (user: ITCIUserInfo) => {
                    this.user = user;
                    this.getUserAppAuth();
                    this.refreshVpnConnections();
                    this.initKeywordFilter();
                },
                error => {
                    console.log("Error getting user information before getting VPN information.", error);
                }
            );
    }

    private addVpnConnection(isJuniper = false): void {
        this.isNew = true;
        this.vpnConnectionToSubmit = lodash.clone(this.EMPTY_VPN_CONNECTION);

        if (isJuniper) {
            this.vpnConnectionToSubmit.vpnProtocol = this.VPN_CONNECTION_PROTOCOLS[this.JUNIPER_NETWORK_CONNECT];
        }

        this.isValidated = false;
        this.isSubmitted = false;
        this.showDialog = true;
    }

    private clearFilter(): void {
        this.keyword = "";
        this.findInVpnConnections();
    }

    private closeDeleteConfirmation(): void {
        this.showDeleteConfirmation = false;
        this.isDeleting = false;
    }

    private closeVpnConnectionDialog(): void {
        this.showDialog = false;
        this.vpnConnectionForm.reset();
    }

    private doDeletion(): void {
        if (this.selectedVpnConnectionId) {
            this.isDeleting = true;
            this
                .vpnSecurity
                .deleteVpnConnection(this.user.uname, this.selectedVpnConnectionId)
                .subscribe(
                    () => {
                        this.closeDeleteConfirmation();
                        this.toastr.success("Deletion successful.");
                        this.refreshVpnConnections();
                    },
                    error => {
                        this.toastr.error("Deletion failed.");
                        console.log(`Failed to delete VPN connection with id: ${this.selectedVpnConnectionId}`, error);
                    }
                );
        }
    }

    private deleteVpnConnection(): void {
        this.showDeleteConfirmation = true;
    }

    private editVpnConnection(): void {
        this.isNew = false;
        this.isValidated = false;
        this.isSubmitted = false;
        this.vpnConnectionToSubmit = lodash.clone(this.selectedVpnConnection);
        this.showDialog = true;
    }

    private findInVpnConnections(): void {
        if (this.keyword) {
            this.vpnConnectionsToDisplay = lodash.filter(
                this.vpnConnections,
                (vpnConnection: ITCIVpnConnection) => lodash.includes(vpnConnection.vpnConnectionName, this.keyword)
            );
        } else {
            this.vpnConnectionsToDisplay = lodash.cloneDeep(this.vpnConnections);
        }

        this.selectVpnConnection();
    }

    private getFullVpnConnectionInfo(): void {
        lodash.forEach(
            this.vpnConnectionBriefs,
            (vpnConnectionBrief: ITCIVpnConnectionBrief) => {
                this
                    .vpnSecurity
                    .getVpnConnectionById(this.user.uname, vpnConnectionBrief.id)
                    .subscribe(
                        (vpnConnection: ITCIVpnConnection) => {
                            if (vpnConnection) {
                                this.vpnConnections.push(vpnConnection);

                                if (!this.keyword || (lodash.includes(vpnConnection.vpnConnectionName, this.keyword))) {
                                    this.vpnConnectionsToDisplay.push(vpnConnection);
                                }

                                if (this.vpnConnections.length === this.vpnConnectionBriefs.length) {
                                    this.isLoading = false;
                                    this.selectVpnConnection();
                                }
                            }
                        },
                        error => {
                            console.log(`Can't get info of VPN with id: ${vpnConnectionBrief.id}`, error);
                        }
                    );
            }
        );
    }

    private getUserAppAuth(): void {
        this
            .userService
            .getAuthForFunction("VPN")
            .subscribe(
                (appAuth: ITCIApplicationAuth) => {
                    this.isReadonly = appAuth.role === USER_ROLES.RO;
                },
                error => {
                    console.log("Failed to get user authentication information.", error);
                }
            );
    }

    private initKeywordFilter(): void {
        this
            .keywordFormControl
            .valueChanges
            .debounceTime(300)
            .subscribe(() => this.findInVpnConnections());
    }

    private refreshVpnConnections(): void {
        this.isLoading = true;
        this
            .vpnSecurity
            .getVpnConnections(this.user.uname)
            .subscribe(
                (vpnConnectionBriefs: ITCIVpnConnectionBrief[]) => {
                    this.vpnConnectionBriefs = vpnConnectionBriefs;
                    this.vpnConnections = [];
                    this.vpnConnectionsToDisplay = [];
                    this.selectedVpnConnectionId = "";
                    this.selectedVpnConnection = null;

                    if (this.vpnConnectionBriefs.length === 0) {
                        this.isLoading = false;
                    } else {
                        this.getFullVpnConnectionInfo();
                    }
                },
                error => {
                    this.isLoading = false;
                    this.toastr.error("Failed to get VPN connections list.");
                    console.log(error);
                }
            );
    }

    private selectVpnConnection(vpnConnectionId?: string): void {
        this.isLoading = true;
        this.selectedVpnConnectionId = "";
        this.selectedVpnConnection = null;

        if (vpnConnectionId) {
            this.selectedVpnConnectionId = vpnConnectionId;
            this.selectedVpnConnection = lodash.find(this.vpnConnections, (vpnConnection: ITCIVpnConnection) => {
                return vpnConnection.vpnConnectionId === this.selectedVpnConnectionId;
            });
        } else {
            if (this.vpnConnectionsToDisplay.length > 0) {
                this.selectedVpnConnectionId = this.vpnConnectionsToDisplay[0].vpnConnectionId;
                this.selectedVpnConnection = lodash.find(this.vpnConnectionsToDisplay, (vpnConnection: ITCIVpnConnection) => {
                    return vpnConnection.vpnConnectionId === this.selectedVpnConnectionId;
                });
            }
        }

        this.isLoading = false;
    }

    private save(): void {
        this.isValidated = true;
        this.isSubmitted = true;

        if (this.vpnConnectionForm.valid) {
            let observable: Observable<any>;
            const duplicateVPN = this.vpnConnections
                .find((connection) => connection.vpnConnectionName === this.vpnConnectionForm.value["connection-name"]);
            if (this.isNew) {
                if (!duplicateVPN) {
                    delete this.vpnConnectionToSubmit.vpnConnectionId;
                    observable = this
                        .vpnSecurity
                        .addVpnConnection(this.user.uname, this.vpnConnectionToSubmit);
                    this.refreshVpnConnection(observable);
                } else {
                    this.toastr.error(`Failed to ${this.isNew ? "add" : "update"} VPN connection.`,
                        `VPN connection having name "${duplicateVPN.vpnConnectionName}" already exist`);
                    this.isSubmitted = false;
                }
            } else {
                if (!duplicateVPN || (duplicateVPN && (this.selectedVpnConnection.vpnConnectionName === duplicateVPN.vpnConnectionName))) {
                    observable = this
                        .vpnSecurity
                        .updateVpnConnection(this.user.uname, this.vpnConnectionToSubmit);
                    this.refreshVpnConnection(observable);
                } else {
                    this.toastr.error(`Failed to ${this.isNew ? "add" : "update"} VPN connection.`,
                        `VPN connection having name "${duplicateVPN.vpnConnectionName}" already exist`);
                    this.isSubmitted = false;
                }
            }

        } else {
            this.toastr.error("Please fill in the required fields.");
            this.isSubmitted = false;
        }
    }

    refreshVpnConnection(observable) {
        observable
            .subscribe(
                () => {
                    console.log(`${this.isNew ? "Added" : "Updated"} VPN connection${!this.isNew && " with id: " +
                    this.vpnConnectionToSubmit.vpnConnectionId}`);
                    this.closeVpnConnectionDialog();
                    this.toastr.success(`Successfully ${this.isNew ? "added" : "updated"} VPN connection.`);
                    this.refreshVpnConnections();
                },
                error => {
                    this.isSubmitted = false;
                    let errorMsg = "Something went wrong! please retry.";
                    try {
                        errorMsg = JSON.parse(error._body).errorMsg;
                    } catch (e) {
                        console.log("Unable to parse error body ", e);
                    }
                    this.toastr.error(`Failed to ${this.isNew ? "add" : "update"} VPN connection.`, errorMsg);
                }
            );
    }
}
